import React, { useEffect } from 'react';
import { getBrandName } from '../utils/brand';

// `c` wraps inline code consistently across this page.
const c = text => <code>{text}</code>;

// Brand-specific install details. The docs site serves both brands by hostname, so the package
// name, config key, env var, tool prefix, and repo/npm links all switch with the brand.
// Note: GreenLine's npm package is `greenline-works-mcp-server` (plain `greenline-mcp-server` is
// taken on npm by an unrelated product), while its GitHub repo is `greenline-mcp-server`.
const BRAND_MCP = {
    GreenLine: {
        pkg: 'greenline-works-mcp-server',
        serverKey: 'greenline',
        env: 'GREENLINE_API_KEY',
        prefix: 'greenline_',
        remote: 'https://mcp.greenline.works/mcp',
        github: 'https://github.com/greenline-works/greenline-mcp-server',
        npm: 'https://www.npmjs.com/package/greenline-works-mcp-server',
    },
    'Kanban Zone': {
        pkg: 'kanban-zone-mcp-server',
        serverKey: 'kanbanzone',
        env: 'KANBANZONE_API_KEY',
        prefix: 'kanbanzone_',
        remote: 'https://mcp.kanbanzone.io/mcp',
        github: 'https://github.com/kanbanzone/kanban-zone-mcp-server',
        npm: 'https://www.npmjs.com/package/kanban-zone-mcp-server',
    },
};

// Single tool entry: name + one-line description + an example prompt the user might type.
const Tool = ({ name, description, example }) => (
    <li className="mcp-tool">
        <code className="mcp-tool-name">{name}</code>
        <span className="mcp-tool-desc"> — {description}</span>
        <div className="mcp-tool-example">
            <em>Try:</em> "{example}"
        </div>
    </li>
);

// Tool names are stored unprefixed; the brand prefix is applied at render. Examples may contain a
// `{brand}` token that is substituted with the brand name.
const TOOL_GROUPS = [
    {
        title: 'Organization & Auth',
        tools: [
            { name: 'get_me', description: 'Verify the API key works and return the organization name.', example: 'Is my {brand} connection working?' },
            { name: 'get_organization', description: 'Fetch the org profile, optionally including members, boards, columns, labels, and custom fields.', example: 'List every board with its columns.' },
        ],
    },
    {
        title: 'Boards',
        tools: [
            { name: 'list_boards', description: 'List all boards in the organization with optional related data.', example: 'What boards do I have?' },
            { name: 'get_board', description: 'Fetch a single board by its publicId.', example: 'Show me the OeMrbG8g board with its columns.' },
            { name: 'list_board_columns', description: 'List columns on a board, with optional title filter.', example: 'List columns on the OeMrbG8g board.' },
            { name: 'list_board_labels', description: 'List labels available on a board.', example: 'What labels are available on this board?' },
            { name: 'list_board_members', description: 'List members who can be assigned cards on a board.', example: 'Who can I assign cards to on this board?' },
            { name: 'list_board_custom_fields', description: 'List custom fields configured on a board.', example: 'What custom fields does this board have?' },
        ],
    },
    {
        title: 'Cards',
        tools: [
            { name: 'create_card', description: 'Create a single card on a board.', example: 'Create a card titled "Refactor auth" on board OeMrbG8g.' },
            { name: 'list_cards', description: 'List cards on a board with filters and pagination.', example: 'What cards are in progress on the OeMrbG8g board?' },
            { name: 'get_card', description: 'Fetch one card by its ObjectId.', example: 'Show me card 6700aabbccddeeff00112233.' },
            { name: 'search_cards', description: 'Full-text search cards by title or number, across the org or one board.', example: 'Find cards mentioning "billing".' },
            { name: 'update_card', description: 'Update fields on an existing card.', example: 'Reassign card 670... to bob@example.com.' },
            { name: 'move_card', description: 'Move a card to a different column (and optionally a different board).', example: 'Move card 670... to the In Progress column.' },
            { name: 'get_card_history', description: 'Fetch the activity history for a card from a given date.', example: 'What happened to card 670... since 2025-01-01?' },
            { name: 'get_card_metrics', description: 'Fetch time-in-column and cycle/lead-time metrics for a card.', example: 'How long has card 670... been in each column?' },
        ],
    },
    {
        title: 'Comments',
        tools: [
            { name: 'create_comment', description: 'Add a comment to a card.', example: 'Add a comment to card 670... saying: blocked on design review.' },
            { name: 'list_card_comments', description: 'List all comments on a card, oldest first.', example: 'What have people said on card 670...?' },
        ],
    },
    {
        title: 'Checklists',
        tools: [
            { name: 'create_checklist', description: 'Create a checklist on a card, optionally pre-populated with tasks (position is taken from array index).', example: 'Add a checklist to card 670... with tasks: write tests, update docs.' },
            { name: 'update_checklist', description: 'Update title or position of a checklist.', example: 'Rename checklist 670... to "QA tasks".' },
            { name: 'list_card_checklists', description: 'List all checklists on a card, including their tasks.', example: "What checklists are on card 670... and what's left to do?" },
        ],
    },
    {
        title: 'Tasks',
        tools: [
            { name: 'create_task', description: 'Add a task to an existing checklist.', example: 'Add a task "Update README" to checklist 670...' },
            { name: 'update_task', description: 'Update fields on an existing task — including completion.', example: 'Mark task 670... as complete.' },
            { name: 'move_task', description: 'Move a task between checklists or reorder within one.', example: 'Move task 670... from checklist A to checklist B at position 0.' },
        ],
    },
    {
        title: 'Labels',
        tools: [
            { name: 'create_label', description: 'Create a label on a board.', example: 'Add a red "Blocked" label to board OeMrbG8g.' },
            { name: 'get_label', description: 'Fetch a single label by its id.', example: 'Show me label 670...' },
            { name: 'update_label', description: "Update a label's name or color.", example: 'Rename label 670... to "Needs review".' },
        ],
    },
];

const TOOL_COUNT = TOOL_GROUPS.reduce((n, group) => n + group.tools.length, 0);

const MCPServer = () => {
    const brandName = getBrandName();
    const cfg = BRAND_MCP[brandName] || BRAND_MCP['Kanban Zone'];
    useEffect(() => {
        document.title = `MCP Server · ${brandName} Docs`;
    }, [brandName]);

    return (
        <div className="swagger-ui doc-page">
            <div className="information-container wrapper">
                <div className="info">
                    <h2 className="title">{brandName} MCP Server</h2>
                    <p>
                        Connect AI assistants like Claude Desktop, Claude Code, Cursor, VS Code, and
                        Windsurf directly to your {brandName} workspace via the{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://modelcontextprotocol.io/"
                        >
                            Model Context Protocol
                        </a>
                        . The server wraps the public API and exposes {TOOL_COUNT} tools for managing
                        boards, cards, comments, checklists, tasks, and labels.
                    </p>

                    <h2 id="overview">Overview</h2>
                    <p>
                        Connect the {brandName} MCP server two ways: point your assistant at the
                        hosted server by URL — nothing to install — or run it locally as a small
                        Node.js process over stdio. Either way it wraps the public API and exposes
                        the tools below to read and modify your workspace.
                    </p>
                    <h3>What you can do</h3>
                    <ul className="circle">
                        <li>Orchestrate workflows — create cards, move them between columns, assign owners.</li>
                        <li>Build reports — pull cycle-time, history, and column metrics into a summary.</li>
                        <li>Draft cards from research — turn meeting notes or specs into a checklist.</li>
                        <li>Manage checklists and tasks — including bulk task creation in one call.</li>
                        <li>Query board state — labels, members, custom fields, columns.</li>
                    </ul>
                    <h3>Supported clients</h3>
                    <ul className="circle">
                        <li><a className="link" href="#claude-desktop">Claude Desktop</a></li>
                        <li><a className="link" href="#claude-code">Claude Code</a></li>
                        <li><a className="link" href="#cursor">Cursor</a></li>
                        <li><a className="link" href="#vs-code">VS Code</a></li>
                        <li><a className="link" href="#windsurf">Windsurf</a></li>
                        <li><a className="link" href="#generic">Any other stdio MCP client</a></li>
                    </ul>
                    <h3>Authentication</h3>
                    <p>
                        The MCP server authenticates to the public API with HTTP Basic, using the
                        same composite API key you'd use for direct API calls. Generate one in{' '}
                        <strong>Org Settings → Integrations → API Key</strong>. The hosted server
                        takes the key in the <code>Authorization</code> header on each request; the
                        local server reads it from an environment variable. A one-click OAuth
                        connector is on the roadmap.
                    </p>
                    <h3>Data access</h3>
                    <p>
                        Your API key gives a connected AI tool visibility across your whole
                        organization, all boards and cards, not just the one you're working in.{' '}
                        {brandName} does not control what a connected client does with that data,
                        whether it retains, logs, or reuses it. Only connect AI tools you trust with
                        your workspace data. The server does not expose delete operations, but it can
                        read, create, and modify cards, comments, checklists, and tasks.
                    </p>
                    <h3>Rate limits</h3>
                    <p>
                        Calls go through the same public API as direct integrations, so the same
                        rate limits apply. See the <a className="link" href="/api">API Reference</a>{' '}
                        for current limits.
                    </p>
                    <h3>Notes</h3>
                    <ul className="circle">
                        <li>Use your API key — your password is never accepted.</li>
                        <li>Two ways to run it: the hosted server at <code>{cfg.remote}</code>, or locally via <code>npx</code>.</li>
                        <li>A one-click OAuth connector for the Claude.ai web client is on the roadmap.</li>
                        <li>The server intentionally does not expose any delete operations.</li>
                    </ul>

                    <h2 id="setup">Setup</h2>
                    <p>
                        Two ways to connect: the <a className="link" href="#hosted">hosted server</a> —
                        a remote URL, nothing to install, recommended for Claude Code and IDE clients —
                        or a <a className="link" href="#local">local install</a> via <code>npx</code>.
                    </p>

                    <h3 id="hosted">Hosted server (remote)</h3>
                    <p>
                        The hosted server lives at {c(cfg.remote)}. It speaks Streamable HTTP and
                        takes your API key in the <code>Authorization</code> header — the same{' '}
                        <code>accessId:apiKey</code>, sent as <code>Bearer accessId:apiKey</code>.
                        Nothing to install.
                    </p>
                    <p><strong>Claude Code</strong> connects to it directly:</p>
                    <pre><code>{`claude mcp add --transport http ${cfg.serverKey} ${cfg.remote} \\
  --header "Authorization: Bearer accessId:apiKey"`}</code></pre>
                    <p>
                        <strong>Claude Desktop, Cursor, and other clients</strong> whose remote-server
                        UI doesn't accept a custom header yet can reach the same URL through the{' '}
                        <code>mcp-remote</code> bridge:
                    </p>
                    <pre><code>{`{
  "mcpServers": {
    "${cfg.serverKey}": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${cfg.remote}",
               "--header", "Authorization:Bearer accessId:apiKey"]
    }
  }
}`}</code></pre>
                    <p>
                        A one-click OAuth connector for Claude Desktop and the Claude.ai web client
                        is on the roadmap.
                    </p>

                    <h3 id="local">Local install (npx)</h3>
                    <h4>Prerequisites</h4>
                    <ul className="circle">
                        <li>Node.js 18 or newer (for <code>npx</code>).</li>
                        <li>An API key from <strong>Org Settings → Integrations → API Key</strong>.</li>
                    </ul>
                    <p>
                        The server is published to npm as{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href={cfg.npm}
                        >
                            <code>{cfg.pkg}</code>
                        </a>
                        . Source on{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href={cfg.github}
                        >
                            GitHub
                        </a>
                        . You don't need to install anything by hand — the snippets below use{' '}
                        <code>npx</code> to fetch and run it on demand.
                    </p>

                    <h3 id="claude-desktop">Claude Desktop</h3>
                    <p>
                        Edit your <code>claude_desktop_config.json</code> — on macOS at{' '}
                        {c('~/Library/Application Support/Claude/claude_desktop_config.json')}, on
                        Windows at {c('%APPDATA%\\Claude\\claude_desktop_config.json')} — and add:
                    </p>
                    <pre><code>{`{
  "mcpServers": {
    "${cfg.serverKey}": {
      "command": "npx",
      "args": ["-y", "${cfg.pkg}"],
      "env": {
        "${cfg.env}": "accessId:apiKey"
      }
    }
  }
}`}</code></pre>
                    <p>Restart Claude Desktop. The {brandName} tools should appear in the tool picker.</p>

                    <h3 id="claude-code">Claude Code</h3>
                    <pre><code>{`claude mcp add ${cfg.serverKey} \\
  -e ${cfg.env}=accessId:apiKey \\
  -- npx -y ${cfg.pkg}`}</code></pre>

                    <h3 id="cursor">Cursor</h3>
                    <p>Edit Cursor's <code>mcp.json</code> in your settings directory and add an entry like:</p>
                    <pre><code>{`{
  "mcpServers": {
    "${cfg.serverKey}": {
      "command": "npx",
      "args": ["-y", "${cfg.pkg}"],
      "env": { "${cfg.env}": "accessId:apiKey" }
    }
  }
}`}</code></pre>

                    <h3 id="vs-code">VS Code</h3>
                    <pre><code>{`code --add-mcp '{"name":"${cfg.serverKey}","command":"npx","args":["-y","${cfg.pkg}"],"env":{"${cfg.env}":"accessId:apiKey"}}'`}</code></pre>

                    <h3 id="windsurf">Windsurf</h3>
                    <p>
                        Open <strong>Settings → MCP servers → Add custom server</strong>, then paste the
                        same JSON shape used for Cursor.
                    </p>

                    <h3 id="generic">Any other stdio MCP client</h3>
                    <p>
                        The server speaks the standard MCP stdio protocol. Run{' '}
                        {c(`npx -y ${cfg.pkg}`)} as a subprocess with {c(cfg.env)} set in the
                        environment. To run from source instead, clone the repo and run{' '}
                        {c('node src/index.js')} after {c('npm install')}.
                    </p>

                    <h3>Troubleshooting</h3>
                    <ul className="circle">
                        <li><strong>401 errors on every tool call</strong> — the API key is wrong or expired. Regenerate it.</li>
                        <li><strong>Tools don't appear</strong> — run <code>{`npx -y ${cfg.pkg}`}</code> once manually to confirm it downloads cleanly, then restart your client.</li>
                        <li><strong>"Card does not exist" on a card you can see</strong> — it's likely a mirror. Pass the <code>board</code> publicId to disambiguate.</li>
                        <li><strong>Truncation warnings on <code>list_cards</code></strong> — paginate with <code>page</code>/<code>count</code>, or narrow with filters.</li>
                    </ul>

                    <h2 id="tools">Tools Reference</h2>
                    <p>
                        {TOOL_COUNT} tools, all prefixed with <code>{cfg.prefix}</code>. Full
                        input/output schemas for the underlying endpoints live in the{' '}
                        <a className="link" href="/api">API Reference</a>. The list below documents
                        agent-facing usage with example prompts.
                    </p>
                    {TOOL_GROUPS.map(group => (
                        <section key={group.title} className="mcp-tool-group">
                            <h3>{group.title} ({group.tools.length})</h3>
                            <ul className="mcp-tool-list">
                                {group.tools.map(tool => (
                                    <Tool
                                        key={tool.name}
                                        name={`${cfg.prefix}${tool.name}`}
                                        description={tool.description}
                                        example={tool.example.replace('{brand}', brandName)}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MCPServer;
